import dayjs from 'dayjs'

import { ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { Cell, ICell } from '.'
import { parseDateInput } from './parsers/datetime'

// Basic types
export type DateFormat = 'full' | 'long' | 'medium' | 'short'
export type DateFormatType = 'fixed' | 'user'
export type DateTimeDisplay = 'datetime' | 'date' | 'time'
export type TimeFormat = 'ampm' | '24h'

// Base interface shared by all configurations
export interface BaseDateTimeSettings {
  dtDisplay: DateTimeDisplay
  dtDateFormatType: DateFormatType
  dtTimezone: string
  dtDisplayTz: boolean
}

// Configuration for datetime with fixed format
export interface DateTimeFixedSettings extends BaseDateTimeSettings {
  dtDisplay: 'datetime'
  dtDateFormatType: 'fixed'
  dtDateFormat: DateFormat
  dtTimeFormat: TimeFormat
  dtDisplaySecond: boolean
  dtLocale: string
}

// Configuration for datetime with user format
export interface DateTimeUserSettings extends BaseDateTimeSettings {
  dtDisplay: 'datetime'
  dtDateFormatType: 'user'
  dtDateFormat: DateFormat
  dtDisplaySecond: boolean
}

// Configuration for date with fixed format
export interface DateFixedSettings extends BaseDateTimeSettings {
  dtDisplay: 'date'
  dtDateFormatType: 'fixed'
  dtDateFormat: DateFormat
  dtLocale: string
}

// Configuration for date with user format
export interface DateUserSettings extends BaseDateTimeSettings {
  dtDisplay: 'date'
  dtDateFormatType: 'user'
  dtDateFormat: DateFormat
}

// Configuration for time with fixed format
export interface TimeFixedSettings extends BaseDateTimeSettings {
  dtDisplay: 'time'
  dtDateFormatType: 'fixed'
  dtTimeFormat: TimeFormat
  dtDisplaySecond: boolean
  dtLocale: string
}

// Configuration for time with user format
export interface TimeUserSettings extends BaseDateTimeSettings {
  dtDisplay: 'time'
  dtDateFormatType: 'user'
  dtDisplaySecond: boolean
}

// Interface for all possible configurations
export interface IDateTimeColumnSettings extends BaseDateTimeSettings {
  dtDateFormat?: DateFormat
  dtTimeFormat?: TimeFormat
  dtDisplaySecond?: boolean
  dtLocale?: string
}

export interface IDateTimeColumn extends ITableColumn {
  Settings: IDateTimeColumnSettings
}

export interface IDateTimeCell extends ICell {
  settings: IDateTimeColumnSettings
  toEditorString: () => string
  rawValue: Cell
  timezone?: string
}

export class DateTimeCell implements IDateTimeCell {
  value: Cell
  rawValue: Cell
  settings: IDateTimeColumnSettings
  timezone?: string

  constructor(value: Cell, settings: IDateTimeColumnSettings, timezone?: string) {
    this.rawValue = value
    this.settings = {
      dtDateFormat: settings.dtDateFormat,
      dtTimeFormat: settings.dtTimeFormat,
      dtDisplaySecond: settings.dtDisplaySecond,
      dtLocale: settings.dtLocale,
      dtDateFormatType: settings.dtDateFormatType,
      dtDisplay: settings.dtDisplay,
      dtDisplayTz: settings.dtDisplayTz,
      dtTimezone: settings.dtTimezone,
    }
    this.value = !value ? null : value instanceof Date ? value : new Date(value as string)
    this.determineTimezone(timezone)
  }

  determineTimezone(defaultTimezone?: string) {
    let tz = defaultTimezone === 'auto' ? undefined : defaultTimezone

    if (typeof this.rawValue === 'string') {
      const tzMatch = this.rawValue.match(/([+-])(\d{2}):(\d{2})$/)
      if (tzMatch) {
        const sign = tzMatch[1]
        const hours = parseInt(tzMatch[2], 10)
        const minutes = parseInt(tzMatch[3], 10)

        let finalTz = ''

        if (hours === 0 && minutes === 0) {
          finalTz = 'UTC'
        } else {
          finalTz = `Etc/GMT${sign === '+' ? '-' : '+'}${hours}`
        }

        tz = finalTz
      }
    }

    this.timezone = tz
  }

  toString(): string {
    if (!this.value || !(this.value instanceof Date) || isNaN(this.value.getTime())) {
      return ''
    }

    const { dtDisplay, dtDateFormat, dtTimeFormat, dtDisplaySecond, dtLocale, dtDisplayTz, dtDateFormatType } =
      this.settings

    if (dtDateFormatType === 'user') {
      switch (dtDisplay) {
        case 'datetime':
          return dayjs(this.value).format(
            dtTimeFormat === 'ampm'
              ? `MM/DD/YYYY hh:mm${dtDisplaySecond ? ':ss' : ''} A`
              : `MM/DD/YYYY HH:mm${dtDisplaySecond ? ':ss' : ''}`
          )
        case 'date':
          return dayjs(this.value).format('MM/DD/YYYY')
        case 'time':
          return dayjs(this.value).format(
            dtTimeFormat === 'ampm' ? `hh:mm${dtDisplaySecond ? ':ss' : ''} A` : `HH:mm${dtDisplaySecond ? ':ss' : ''}`
          )
      }
    }

    const options: Intl.DateTimeFormatOptions = {
      timeZone: this.timezone || undefined,
    }

    switch (dtDisplay) {
      case 'datetime':
        if (dtDisplayTz) {
          options.year = 'numeric'
          options.month = 'short'
          options.day = 'numeric'
          options.hour = 'numeric'
          options.minute = 'numeric'
          options.second = dtDisplaySecond ? 'numeric' : undefined
          options.timeZoneName = 'short'
        } else {
          options.year = 'numeric'
          switch (dtDateFormat) {
            case 'full':
              options.month = 'long'
              options.day = 'numeric'
              options.weekday = 'long'
              break
            case 'long':
              options.month = 'long'
              options.day = 'numeric'
              break
            case 'medium':
              options.month = 'short'
              options.day = 'numeric'
              break
            case 'short':
              options.month = 'numeric'
              options.day = 'numeric'
              break
          }
          options.hour = 'numeric'
          options.minute = 'numeric'
          options.second = dtDisplaySecond ? 'numeric' : undefined
        }
        options.hour12 = dtTimeFormat === 'ampm'
        break
      case 'date':
        options.year = 'numeric'
        switch (dtDateFormat) {
          case 'full':
            options.month = 'long'
            options.day = 'numeric'
            options.weekday = 'long'
            break
          case 'long':
            options.month = 'long'
            options.day = 'numeric'
            break
          case 'medium':
            options.month = 'short'
            options.day = 'numeric'
            break
          case 'short':
            options.month = 'numeric'
            options.day = 'numeric'
            break
        }
        break
      case 'time':
        options.hour = 'numeric'
        options.minute = 'numeric'
        options.second = dtDisplaySecond ? 'numeric' : undefined
        options.hour12 = dtTimeFormat === 'ampm'
        if (dtDisplayTz) {
          options.timeZoneName = 'short'
        }
        break
    }

    let formatter: Intl.DateTimeFormat
    try {
      formatter = new Intl.DateTimeFormat(dtLocale, options)
    } catch (error) {
      formatter = new Intl.DateTimeFormat(undefined, options)
    }

    try {
      let formatted = formatter.format(this.value)

      if (dtDisplay === 'datetime' && dtDisplayTz) {
        const parts = formatted.split(' ')
        const date = parts.slice(0, 3).join(' ')
        const time = parts.slice(3).join(' ')
        formatted = `${date}, ${time}`
      }

      // delete double comma
      formatted = formatted.replace(',,', ',')

      // delete comma at the end
      formatted = formatted.replace(/,\s*$/, '')

      return formatted
    } catch (err) {
      return ''
    }
  }

  toCell<T extends Cell>(newValue: T) {
    if (!newValue) {
      this.value = ''
    } else {
      if (typeof newValue === 'string') {
        const dateStringInputValue = newValue as string

        let updatedValue

        try {
          const parsedDate = parseDateInput(dateStringInputValue)

          if (parsedDate) {
            // Parse the date string directly in the target timezone
            // instead of parsing in local timezone and then converting
            const FORMAT_STRING = 'YYYY-MM-DD HH:mm:ss'
            const formatted = parsedDate.format(FORMAT_STRING)
            updatedValue = this.timezone ? dayjs.tz(formatted, FORMAT_STRING, this.timezone).format() : formatted
          } else {
            updatedValue = this.timezone ? dayjs().tz(this.timezone).format() : dayjs().format()
          }
        } catch (err) {
          console.log('err', err, { dateStringInputValue, timezone: this.timezone })
        }

        this.value = updatedValue
        //
      } else if (newValue instanceof Date) {
        this.value = newValue
      }
    }

    return this as DateTimeCell
  }

  convertToTimezoneInstance(val: any) {
    const instance = this.timezone ? dayjs(val).tz(this.timezone) : dayjs(val)
    return instance
  }

  toEditorString(): string {
    if (!this.value || !(this.value instanceof Date) || isNaN(this.value.getTime())) {
      return ''
    }

    const { dtDisplay, dtTimeFormat, dtDisplaySecond } = this.settings

    if (!dtDisplay && !dtTimeFormat && !dtDisplaySecond) {
      return this.convertToTimezoneInstance(this.value).format(`YYYY-MM-DD`)
    }

    switch (dtDisplay) {
      case 'datetime':
        if (dtTimeFormat === '24h') {
          return this.convertToTimezoneInstance(this.value).format(`YYYY-MM-DD HH:mm${dtDisplaySecond ? ':ss' : ''}`)
        } else {
          return this.convertToTimezoneInstance(this.value).format(`YYYY-MM-DD hh:mm${dtDisplaySecond ? ':ss' : ''} A`)
        }
      case 'date':
        return this.convertToTimezoneInstance(this.value).format('YYYY-MM-DD')

      case 'time':
        return this.convertToTimezoneInstance(this.value).format('HH:mm:ss')
    }
  }
}
